import React from "react";
import { Box, Chip, Paper, Stack, Typography } from "../../Collections/Imports";
import { formatMoney } from "./treeLogic";

/** Baseline preview; runtime tier strategies and eligibility can change the actual reached order. */
export const WalkOrderPanel = ({ walk, manualOrder, leadPrice, tierOrder }) => (
  <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="walk-order">
    <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
      Walk order ({walk.length})
    </Typography>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
      {manualOrder
        ? "Manual order: list order within posted buyers and within exit offers; tiers, prices and priorities are ignored."
        : ["weighted_random", "even_rotation"].includes(tierOrder)
        ? `Baseline preview for ${tierOrder}; runtime selection changes order within each tier when a lead reaches it.`
        : "Tier first (1 is tried first), then price (highest first), then priority (highest first). First buyer to accept wins."}
      {" Exit offers are tried after posted buyers. Save checks current campaign roles and eligibility."}
      {!manualOrder &&
        (leadPrice === null || leadPrice === ""
          ? " Campaigns without a price are shown last in their tier; for a real lead they rank at the lead's price."
          : ` Campaigns without a price rank at the preview lead price (${formatMoney(leadPrice)}).`)}
    </Typography>
    <Box component="ol" sx={{ pl: 3, m: 0, maxHeight: 520, overflowY: "auto" }}>
      {walk.map((s) => {
        const skipped = !s.active || s.capped;
        return (
          <Box component="li" key={s.campaignId} sx={{ py: 0.25, color: skipped ? "text.disabled" : "inherit" }}>
            <Typography variant="body2" component="span" sx={{ textDecoration: skipped ? "line-through" : "none" }}>
              {s.buyerName || "?"} · {s.campaignName}
            </Typography>{" "}
            <Typography variant="caption" color="text.secondary" component="span">
              {s.tier === null || s.tier === undefined ? "no tier" : `T${s.tier}`} · {formatMoney(s.price)} · p{s.priority ?? 0}
            </Typography>
            {skipped && <Chip size="small" sx={{ ml: 0.5, height: 18 }} label={!s.active ? "paused" : "capped"} />}
          </Box>
        );
      })}
    </Box>
  </Paper>
);

/**
 * Read-only version history. Task CU-LS-M1-054 (tree versions) has no read endpoint yet; when it does, pass its
 * versions in (newest first: {version, createdOn, changedBy, change}) and they are listed here.
 */
export const VersionHistoryPanel = ({ versions, currentVersion }) => (
  <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="version-history">
    <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
      Version history
    </Typography>
    {Array.isArray(versions) && versions.length > 0 ? (
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {versions.map((v) => (
          <Typography key={v.version} variant="body2">
            v{v.version} · {v.createdOn} · {v.changedBy || "unknown"} · {v.change}
          </Typography>
        ))}
      </Stack>
    ) : (
      <Typography variant="caption" color="text.secondary" display="block">
        {currentVersion ? `Current version: ${currentVersion}. ` : ""}
        Not available yet: tree versions arrive with task CU-LS-M1-054. Until then, use Export before big changes as a
        backup.
      </Typography>
    )}
  </Paper>
);
